import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { CreateReceiptResponse } from "@/api/types";
import { ConflictError } from "@/api/client";
import { useCreateReceipt } from "@/hooks/useReceipts";
import { PageShell } from "@/components/PageShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/Button";
import { UploadClosedCard } from "@/components/UploadClosedCard";
import "./receipt-upload.css";

// "closed" is the 409 trip_closed race (AC-06.8): a distinct info state,
// never the generic "error" retry card.
type Phase = "pick" | "compress" | "upload" | "done" | "error" | "closed";

const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED_MIME: ReadonlyArray<string> = ["image/jpeg", "image/png", "image/heic"];

export function ReceiptUploadScreen() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>("pick");
  const [errorMsg, setErrorMsg] = useState<string>();

  const create = useCreateReceipt(slug);

  // Auto-click the file picker on mount per AC-06.1 ("FAB tap → camera roll").
  useEffect(() => {
    fileRef.current?.click();
  }, []);

  async function handleFile(file: File) {
    if (file.size > MAX_BYTES) {
      setErrorMsg("Фото більше 8 МБ. Стисни або вибери інше.");
      setPhase("error");
      return;
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      setErrorMsg("Підтримуються JPG, PNG, HEIC.");
      setPhase("error");
      return;
    }
    setPhase("compress");
    // Stub: real compression is owned by ADR-0013; for the mock we just wait.
    await new Promise<void>((r) => setTimeout(r, 250));

    setPhase("upload");
    let created: CreateReceiptResponse;
    try {
      created = await create.mutateAsync({
        file_name: file.name,
        mime_type: file.type as "image/jpeg" | "image/png" | "image/heic",
        byte_size: file.size,
      });
    } catch (err) {
      // Distinguish the 409 trip_closed race (AC-06.8): another visitor on
      // the shared link closed the trip between the FAB tap and this create
      // call. The backend guard runs before presign/PUT, so no S3 object and
      // no DDB row exist — show the info card, never the generic retry card.
      if (err instanceof ConflictError && err.conflictReason === "trip_closed") {
        setPhase("closed");
        return;
      }
      setErrorMsg("Не вдалось завантажити фото. Спробуй ще.");
      setPhase("error");
      return;
    }

    // Issue the presigned PUT. Per ADR-0021, this is the SPA's last step
    // in the happy path — the S3 ObjectCreated event triggers
    // `parse-worker` server-side and the detail screen polls the receipt
    // row to watch `pending_upload → parsing → parsed`.
    let putRes: Response;
    try {
      putRes = await fetch(created.upload.url, {
        method: "PUT",
        headers: created.upload.headers,
        body: file,
      });
    } catch {
      // Network error / CORS / offline. Surface to the operator instead
      // of silently swallowing — see ADR-0021 §"What gets harder".
      setErrorMsg("Не вдалось завантажити фото. Спробуй ще.");
      setPhase("error");
      return;
    }
    if (!putRes.ok) {
      setErrorMsg("Не вдалось завантажити фото. Спробуй ще.");
      setPhase("error");
      return;
    }

    setPhase("done");
    // Replace, not push (NFR-NAV-3 / AC-06.11): the upload screen is
    // transient and its mount effect auto-reopens the file picker. Pushing
    // would leave it on the stack so a browser-back from the detail screen
    // re-fires the picker — a dead-end loop. Replacing removes it from
    // history, so back from detail lands on the receipts list instead.
    navigate(`/t/${slug}/receipts/${created.receipt.receipt_id}`, { replace: true });
  }

  function onCancel() {
    navigate(`/t/${slug}/receipts`);
  }

  if (phase === "closed") {
    // Trip was closed mid-flow (409 trip_closed). Distinct info state — no
    // stepper, no retry, no danger card. Single [ Закрити ] → receipts list.
    return (
      <PageShell header={<BrandHeader />}>
        <div className="wp-upload">
          <UploadClosedCard onClose={onCancel} />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell header={<BrandHeader />}>
      <div className="wp-upload">
        <button type="button" className="wp-upload__close" onClick={onCancel}>
          ✕ Скасувати
        </button>
        <Stepper phase={phase} />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/heic"
          hidden
          onChange={(e) => {
            const f = e.currentTarget.files?.[0];
            if (f) void handleFile(f);
          }}
          data-testid="upload-input"
        />
        {phase === "pick" ? (
          <Button size="lg" onClick={() => fileRef.current?.click()}>
            Вибрати фото
          </Button>
        ) : null}
        {phase === "error" ? (
          <>
            <p className="wp-upload__error" role="alert" data-testid="upload-error">
              {errorMsg}
            </p>
            <Button
              onClick={() => {
                setErrorMsg(undefined);
                setPhase("pick");
                // Re-open the system picker so the user can pick a new
                // (or the same) file without an extra tap. Reset .value
                // so re-picking the same file still fires `onChange`.
                if (fileRef.current) {
                  fileRef.current.value = "";
                  fileRef.current.click();
                }
              }}
              variant="secondary"
              data-testid="upload-retry"
            >
              Спробувати ще
            </Button>
          </>
        ) : null}
        {phase === "done" ? (
          <p className="wp-upload__hint">
            Готово. Перекидаємо на сторінку чеку…
          </p>
        ) : null}
        {(phase === "compress" || phase === "upload") && (
          <p className="wp-upload__hint">
            {phase === "compress" ? "Стискаю…" : "Завантажую…"}
          </p>
        )}
      </div>
    </PageShell>
  );
}

function Stepper({ phase }: { phase: Phase }) {
  // ADR-0021 collapses the upload flow to two SPA-driven phases:
  // compress + PUT. Parsing is server-driven (S3 → parse-worker) and is
  // shown by the receipt-detail screen's spinner branch.
  const order: Phase[] = ["compress", "upload"];
  const idx = order.indexOf(phase);
  return (
    <ol className="wp-stepper" aria-label="Прогрес завантаження">
      {order.map((p, i) => {
        const active = i <= idx;
        return (
          <li
            key={p}
            className={[
              "wp-stepper__dot",
              active ? "wp-stepper__dot--active" : null,
            ]
              .filter(Boolean)
              .join(" ")}
            aria-current={p === phase ? "step" : undefined}
          >
            <span className="visually-hidden">{p}</span>
          </li>
        );
      })}
    </ol>
  );
}
